<template lang="pug">
v-app#inspire
    //- `fullscreen` route meta strips the app chrome — classification
    //- band, nav menu, status bar, chat button. Used by the PDF print
    //- view (/print/plan) so the exported document doesn't have random
    //- app-bar text bleeding onto every page.
    //-
    //- Mobile shell: on smAndDown (< 960 px) the banner, footer, and
    //- chat FAB are hidden and the nav menu becomes a temporary drawer
    //- toggled via a small floating hamburger — the map gets the full
    //- viewport. See `useIsMobile` for the detection contract.
    v-app-bar.classification-banner(v-if='!isFullscreen && !isMobile' app :elevation='0' :height='28')
        v-toolbar-title.classification-text.w-100 {{ bannerText }}
    BaseNavigationMenu(
        v-if='menuItems && !isFullscreen'
        v-model:mobileOpen='mobileDrawerOpen'
        :menuItems='menuItems'
        :isMobile='isMobile'
    )
    //- Mobile hamburger — top-left, over the map. Only rendered on
    //- mobile because the desktop nav is permanently visible so
    //- there's nothing to toggle.
    v-btn.mobile-nav-toggle(
        v-if='!isFullscreen && isMobile'
        icon='mdi-menu'
        size='small'
        variant='elevated'
        color='primary'
        title='Menu'
        @click='mobileDrawerOpen = !mobileDrawerOpen'
    )
    v-main
        //- Router-view key: within a single operation we want sibling
        //- child-route changes (chart → plan → chart) to preserve the
        //- KeepAlive cache in OperationLayout (see that file). But
        //- switching operations MUST force a full remount so state
        //- from op A never leaks into op B. So: key on operationId
        //- when it's part of the current route, else fall back to the
        //- full path so /login / /login-callback / /logout etc. still
        //- get their own unique remount.
        router-view(:key='($route.params.operationId as string) || $route.fullPath')
    v-footer.app-status-bar(v-if='!isFullscreen && !isMobile' app :elevation='0' :height='26' style='padding: 0;')
        AppStatusBar
    notification-snackbar(v-if='!isFullscreen')
    TutorialHost(v-if='!isFullscreen && !isMobile')
    //- Chorus Assistant. Globally-mounted FAB toggles the ChatPanel
    //- drawer. Hidden on fullscreen routes so the PDF print view stays
    //- clean. Panel handles its own send/reset lifecycle. Hidden on
    //- mobile to reclaim thumb-reach real estate for the map.
    v-btn(
        v-if='!isFullscreen && !isMobile'
        icon='mdi-robot-outline'
        color='primary'
        size='large'
        elevation='6'
        style='position: fixed; right: 20px; bottom: 40px; z-index: 100;'
        title='Chorus Assistant'
        @click='chatOpen = !chatOpen'
    )
    ChatPanel(v-if='!isMobile' v-model='chatOpen')
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import NotificationSnackbar from '@/common/AppNotificationSnackbar.vue';
import BaseNavigationMenu from '@/common/BaseNavigationMenu.vue';
import AppStatusBar from '@/common/AppStatusBar.vue';
import TutorialHost from '@/tutorial/TutorialHost.vue';
import ChatPanel from '@/catlAssistant/ChatPanel.vue';
import { useMapboxStore } from '@/store/storeMapbox';
import { useOperationStore } from '@/store/storeOperation';
import { useIsMobile } from '@/composables/useIsMobile';

const route = useRoute();
const mapboxStore = useMapboxStore();
const operationStore = useOperationStore();
const isMobile = useIsMobile();

const menuItems = computed(() => route.meta.menuItems);
const isFullscreen = computed(() => route.meta.fullscreen === true);
const chatOpen = ref(false);
// Mobile-only drawer state — driven by App.vue's hamburger button
// and passed into BaseNavigationMenu, which reflects it into the
// v-navigation-drawer's v-model.
const mobileDrawerOpen = ref(false);

// Top classification banner — derived from the current operation's
// MaximumClassification (Operation.cs). Null means "operation didn't
// declare one" → fall back to the default NAVWARN string so the band
// never renders empty.
const bannerText = computed(() =>
    operationStore.currentOperation?.maximumClassification
    ?? 'UNCLASSIFIED // NOT FOR NAVIGATIONAL USE');

// Fire the app-level bootstrap fetches once we actually have an access
// token in localStorage. In prod, the Vue app mounts while /login-callback
// is still processing the auth code — before tokens are stored — so any
// query fired synchronously in onMounted 401s. Poll localStorage a couple
// of times and fetch when the token lands; also fetch immediately if the
// token is already there (already-authenticated reload).
let bootstrapTimer: ReturnType<typeof setInterval> | null = null;
async function bootstrap() {
    mapboxStore.fetchToken();
    await operationStore.fetchOperations();
}
onMounted(() => {
    if (!import.meta.env.PROD || localStorage.getItem('access-token')) {
        void bootstrap();
        return;
    }
    bootstrapTimer = setInterval(() => {
        if (localStorage.getItem('access-token')) {
            if (bootstrapTimer !== null) { clearInterval(bootstrapTimer); bootstrapTimer = null; }
            void bootstrap();
        }
    }, 250);
});
onUnmounted(() => {
    if (bootstrapTimer !== null) { clearInterval(bootstrapTimer); bootstrapTimer = null; }
});
</script>

<style lang="scss">
//  Importing global styles
@use '../src/common/graph-components/graphStyles.module.scss';

.classification-banner {
    background-color: #4CAF50 !important; /* Green background */
}

.classification-text {
    width: 100%;
    text-align: center;
    color: white !important;
    font-weight: bold;
    font-size: 14px;
    letter-spacing: 0.5px;
}

/* Mobile hamburger — floats above the map at top-left. z-index has
 * to clear both mapbox controls (>= 4) and the schedule ribbon (5)
 * but stay below dialogs / snackbars (>= 1000). */
.mobile-nav-toggle {
    position: fixed !important;
    top: 8px;
    left: 8px;
    z-index: 20;
}
</style>
